import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';

type LessonField = keyof Omit<Lesson, 'id'>;

interface LessonsState {
	lessons: LessonSection[];
	activeGroupId: string | null;
	activeLessonId: string | null;
	setLessonPlans: (plans: LessonSection[]) => void;
	setActiveGroup: (groupId: string) => void;
	setActiveLesson: (lessonId: string) => void;
	addLessonSection: (title: string) => void;
	addLesson: () => void;
	removeGroup: (groupId: string) => void;
	removeLesson: (groupId: string, lessonId: string) => void;
	updateGroupTitle: (groupId: string, title: string) => void;
	updateLessonField: (groupId: string, lessonId: string, field: LessonField, value: string) => void;
}

const makeLesson = (title = 'New Lesson'): Lesson => ({
	id: uuidv4(),
	title,
	objectives: '',
	body: '',
});

export const useCourseLessonsStore = create<LessonsState>((set, get) => ({
	lessons: [],
	activeGroupId: null,
	activeLessonId: null,

	setLessonPlans: (plans) => {
		const firstGroup = plans[0] ?? null;
		const firstLesson = firstGroup?.lessons[0] ?? null;
		set({
			lessons: plans,
			activeGroupId: firstGroup?.id ?? null,
			activeLessonId: firstLesson?.id ?? null,
		});
	},

	setActiveGroup: (groupId) => {
		const { lessons } = get();
		const group = lessons.find((g) => g.id === groupId);
		set({
			activeGroupId: groupId,
			activeLessonId: group?.lessons[0]?.id ?? null,
		});
	},

	setActiveLesson: (lessonId) => {
		set({ activeLessonId: lessonId });
	},

	updateGroupTitle: (groupId, title) => {
		const { lessons } = get();
		set({
			lessons: lessons.map((g) => (g.id === groupId ? { ...g, title } : g)),
		});
	},

	updateLessonField: (groupId, lessonId, field, value) => {
		const { lessons } = get();
		set({
			lessons: lessons.map((g) =>
				g.id === groupId
					? {
							...g,
							lessons: g.lessons.map((l) => (l.id === lessonId ? { ...l, [field]: value } : l)),
						}
					: g
			),
		});
	},

	addLessonSection: (title) => {
		const { lessons } = get();
		const newLesson = makeLesson();
		const newGroup: LessonSection = { id: uuidv4(), title, lessons: [newLesson] };
		set({
			lessons: [...lessons, newGroup],
			activeGroupId: newGroup.id,
			activeLessonId: newLesson.id,
		});
	},

	addLesson: () => {
		const { lessons, activeGroupId } = get();
		const newLesson = makeLesson();
		set({
			lessons: lessons.map((g) =>
				g.id === activeGroupId ? { ...g, lessons: [...g.lessons, newLesson] } : g
			),
			activeLessonId: newLesson.id,
		});
	},

	removeGroup: (groupId) => {
		const { lessons } = get();
		const remaining = lessons.filter((g) => g.id !== groupId);
		const lastGroup = remaining[remaining.length - 1] ?? null;
		set({
			lessons: remaining,
			activeGroupId: lastGroup?.id ?? null,
			activeLessonId: lastGroup?.lessons[0]?.id ?? null,
		});
	},

	removeLesson: (groupId, lessonId) => {
		const { lessons, activeLessonId } = get();
		const group = lessons.find((g) => g.id === groupId);
		if (!group) return;

		const remainingLessons = group.lessons.filter((l) => l.id !== lessonId);

		if (remainingLessons.length === 0) {
			// Remove the whole group if last lesson is deleted
			const remainingGroups = lessons.filter((g) => g.id !== groupId);
			const lastGroup = remainingGroups[remainingGroups.length - 1] ?? null;
			set({
				lessons: remainingGroups,
				activeGroupId: lastGroup?.id ?? null,
				activeLessonId: lastGroup?.lessons[0]?.id ?? null,
			});
			return;
		}

		const newActiveLessonId =
			activeLessonId === lessonId ? (remainingLessons[0]?.id ?? null) : activeLessonId;

		set({
			lessons: lessons.map((g) => (g.id === groupId ? { ...g, lessons: remainingLessons } : g)),
			activeLessonId: newActiveLessonId,
		});
	},
}));
